/** Shared utilities for {@link ParentRow}, {@link ParentCard} and {@link ParentsDataTable}. */

import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';
import { Mail, Phone, type LucideIcon } from 'lucide-react';
import {
  isReactivatableUserStatus,
  type FolderSituation,
  type OnboardingStatus,
  type PrimaryContactKind,
  type WeeklyAvailabilitySlot,
} from '@/types/user.types';
import { resolveUserContacts } from '@/utils/user-contact-utils';
import { flattenProfessionalSituation } from '@/utils/professional-situation-utils';
import {
  ACTIVITY_SECTOR_LABEL,
  CIVIL_SERVICE_BRANCH_LABEL,
  FIELD_OF_ACTIVITY_TUTOR_LABEL,
  HIGH_SCHOOL_CURRICULUM_LABEL,
  STUDY_LEVEL_LABEL,
  TUTORING_COURSE_LABEL,
  PARENT_TAB_USER_STATUS,
  type ParentFilters,
  type ParentTabView,
} from '@/components/parents/parent-configs';
import type { ParentRecord } from '@/types/parent.types';
export {
  completionBarColor,
  getCivilTitleLabel,
  getCompletionStatus,
  getStudentCategory,
  getStudentSituation,
} from '@/utils/common-utils';

/** RBAC actions available at the Row / Card level for parents. */
export interface ParentRowPageActions {
  canEditRow?: boolean;
  canViewRow?: boolean;
  canInviteChild?: boolean;
  canViewStudents?: boolean;
  canArchiveRow?: boolean;
  canReactivateRow?: boolean;
}

/** RBAC actions available at the {@link DataTable} level for parents. */
export interface ParentDataTablePageActions extends ParentRowPageActions {}

/** Primary contact icon + value, with optional secondary shown under it. */
export interface ParentPrimaryContact {
  icon: LucideIcon;
  value: string;
  secondaryValue: string | null;
}

/** One display row for a flattened professional-situation field. */
type ParentProSitField = {
  key: string;
  label: string;
  value: string;
  kind?: 'text' | 'yesNo';
};

/** Department code and display name. */
type DepartmentOption = { code: string; name: string };

/** List row id, or a user id already passed in, for the full-state GET. */
export function resolveParentExpandedUserId(
  expandedRowId: string | null | undefined,
  rows: readonly ParentRecord[],
): string | undefined {
  if (!expandedRowId) return undefined;
  const foundById = rows.find((row) => row.id === expandedRowId);
  if (foundById) return foundById.userId;
  const foundByUserId = rows.find((row) => row.userId === expandedRowId);
  return foundByUserId?.userId ?? expandedRowId;
}

/** Hide archive when the row is already archived. */
export function canShowArchiveRow(
  status: string | undefined,
  canArchiveRow: boolean | undefined,
): boolean {
  return !!canArchiveRow && status !== 'archived';
}

/** Hide reactivate on pre-registered or non-reactivatable statuses. */
export function canShowReactivateRow(
  status: string | undefined,
  tab: string,
  canReactivateRow: boolean | undefined,
): boolean {
  return !!canReactivateRow && tab !== 'pre_registered' && isReactivatableUserStatus(status);
}

/** Formats a date as dd/mm/yyyy, or "—". */
export function formatParentDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Phone vs email; section `basic_information` wins over the user phone. */
export function deriveParentContact(
  sections: Record<string, unknown>,
  userPhone?: string | null,
  userEmail?: string | null,
  primaryContactKind?: PrimaryContactKind | null,
): ParentPrimaryContact {
  const basicInfo = sections['basic_information'] as Record<string, unknown> | undefined;
  const rawPhone = basicInfo?.['phone'] ?? basicInfo?.['phoneNumber'] ?? userPhone;
  const contactPhone = typeof rawPhone === 'string' && rawPhone ? rawPhone : userPhone;
  const resolved = resolveUserContacts(primaryContactKind, userEmail, contactPhone);
  return {
    icon: resolved.kind === 'phone' ? Phone : Mail,
    value: resolved.primary?.value ?? 'N/R',
    secondaryValue: resolved.secondary?.value ?? null,
  };
}

/** Returns the registration manager's first + last name, or "—". */
export function formatRegistrationManagerName(record: ParentRecord): string {
  if (!record.registrationManager) return '—';
  return `${record.registrationManager.firstName} ${record.registrationManager.lastName}`;
}

/**
 * Normalises a {@link ManagedByValue} filter to the string[] format expected by the API.
 * Returns undefined when no meaningful value is set.
 */
export function buildManagedByParam(
  managedByIds: ManagedByValue | undefined,
): string[] | undefined {
  if (managedByIds === undefined) return undefined;
  if (managedByIds === 'all') return ['all'];
  if (managedByIds === 'unmanaged') return ['unmanaged'];
  if (Array.isArray(managedByIds) && managedByIds.length > 0) return managedByIds;
  return undefined;
}

/** Map UI filters + tab to parent list params (tab-specific dates / steps). */
export function buildParentRemoteListFilters(filters: ParentFilters, activeTab: ParentTabView) {
  return {
    onboardingStatus: filters.onboardingStatus as OnboardingStatus | undefined,
    folderSituation: filters.folderSituation as FolderSituation | undefined,
    managedByIds: buildManagedByParam(filters.managedByIds),
    search: filters.search,
    registrationDateFrom:
      activeTab === 'parents'
        ? filters.parentsRegistrationDateFrom
        : filters.preRegisteredRegistrationDateFrom,
    registrationDateTo:
      activeTab === 'parents'
        ? filters.parentsRegistrationDateTo
        : filters.preRegisteredRegistrationDateTo,
    stepKeys: activeTab === 'pre_registered' ? filters.stepKeys : undefined,
    childrenCount: activeTab === 'parents' ? filters.childrenCount : undefined,
    userStatus: PARENT_TAB_USER_STATUS[activeTab],
  };
}

/** True when any parent chip / date / search filter is set for the tab. */
export function hasParentRemoteActiveFilters(
  filters: ParentFilters,
  activeTab: ParentTabView,
): boolean {
  const registrationDateFrom =
    activeTab === 'parents'
      ? filters.parentsRegistrationDateFrom
      : filters.preRegisteredRegistrationDateFrom;
  const registrationDateTo =
    activeTab === 'parents'
      ? filters.parentsRegistrationDateTo
      : filters.preRegisteredRegistrationDateTo;
  return (
    !!filters.onboardingStatus ||
    !!filters.folderSituation ||
    !!filters.managedByIds ||
    !!filters.search ||
    !!registrationDateFrom ||
    !!registrationDateTo ||
    (activeTab === 'pre_registered' && (filters.stepKeys?.length ?? 0) > 0) ||
    (activeTab === 'parents' && filters.childrenCount !== undefined) ||
    !!filters.userStatus
  );
}

/** Formats a date as dd/mm/yy, or "—". */
export function formatDate(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

/** Days elapsed since `dateStr`, or "—". */
export function daysSince(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  return `${days} jour${days === 1 ? '' : 's'}`;
}

/** Capitalize the first letter, or "—". */
export function capitalizeFirst(str?: string): string {
  if (!str) return '—';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** `"zipCode city"` trimmed, or "—". */
export function formatCityInfo(zipCode: string | undefined, city: string | undefined): string {
  if (!zipCode && !city) return '—';
  return `${zipCode ?? ''} ${city ?? ''}`.trim();
}

/** Program name/abbreviation, or a short fallback. */
export function getProgramLabel(programData: any): string {
  if (!programData) return '—';
  if (typeof programData === 'string') return programData;
  return programData.programName ?? programData.name ?? programData.abbreviation ?? '?';
}

/** Normalize day/start/end aliases onto {@link WeeklyAvailabilitySlot}. */
export function normalizeSlots(raw: any[]): WeeklyAvailabilitySlot[] {
  return (raw ?? []).map((s) => ({
    day: s.day ?? s.dayOfTheWeek,
    start: String(s.start ?? s.startTime ?? ''),
    end: String(s.end ?? s.endTime ?? ''),
  }));
}

/** Open a document URL in a new tab via a blob preview. */
export async function openDocumentPreview(url: string): Promise<void> {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const pdfBlob = new Blob([blob], { type: blob.type || 'application/pdf' });
    const objectUrl = URL.createObjectURL(pdfBlob);
    const w = window.open(objectUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    if (!w) window.open(url, '_blank', 'noopener,noreferrer');
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/** Download a URL as a named file via blob. */
export async function downloadViaBlob(url: string, filename: string): Promise<void> {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank');
  }
}

/** Label for a field-of-activity code. */
function fieldOfActivityLabel(value: string | undefined): string {
  if (!value) return '';
  return ACTIVITY_SECTOR_LABEL[value] ?? FIELD_OF_ACTIVITY_TUTOR_LABEL[value] ?? value;
}

/** Label for a study-level code. */
function studyLevelLabel(value: string | undefined): string {
  if (!value) return '';
  return STUDY_LEVEL_LABEL[value] ?? value;
}

/** Resolve a department code to its name. */
function departmentName(
  code: string | undefined,
  departments: DepartmentOption[],
): string | undefined {
  if (!code) return undefined;
  return departments.find((item) => item.code === code)?.name ?? code;
}

/** Company or employer name from professional situation. */
function enterpriseName(proSit: Record<string, any>): string | undefined {
  return (
    proSit.companyName ||
    proSit.company?.[0]?.employerName ||
    proSit.company?.[0]?.establishmentName
  );
}

/** Append a professional-situation field when the value is set. */
function pushProSitField(
  fields: ParentProSitField[],
  key: string,
  label: string,
  value: string | undefined,
  kind: ParentProSitField['kind'] = 'text',
): void {
  if (!value) return;
  fields.push({ key, label, value, kind });
}

/** Professional-situation fields for a high-school student. */
function getHighSchoolStudentProFields(proSit: Record<string, any>): ParentProSitField[] {
  const fields: ParentProSitField[] = [];
  pushProSitField(
    fields,
    'establishment',
    'Établissement',
    proSit.establishmentName || proSit.establishment,
  );
  pushProSitField(fields, 'level', "Niveau d'études", studyLevelLabel(proSit.level));
  if (proSit.curriculum) {
    pushProSitField(
      fields,
      'curriculum',
      'Cursus scolaire',
      HIGH_SCHOOL_CURRICULUM_LABEL[proSit.curriculum] ?? proSit.curriculum,
    );
  }
  pushProSitField(
    fields,
    'engagement',
    'Engagement associatif',
    proSit.associativeEngagement,
    'yesNo',
  );
  return fields;
}

/** Professional-situation fields for a student. */
function getStudentProFields(proSit: Record<string, any>): ParentProSitField[] {
  const fields: ParentProSitField[] = [];
  pushProSitField(
    fields,
    'establishment',
    "Établissement d'étude",
    proSit.establishmentName || proSit.establishment,
  );
  pushProSitField(fields, 'level', "Niveau d'études", studyLevelLabel(proSit.level));
  pushProSitField(
    fields,
    'engagement',
    'Engagement associatif',
    proSit.associativeEngagement,
    'yesNo',
  );
  if (proSit.studentCurriculum) {
    pushProSitField(
      fields,
      'studentCurriculum',
      'Cursus étudiant',
      TUTORING_COURSE_LABEL[proSit.studentCurriculum] ?? proSit.studentCurriculum,
    );
  }
  pushProSitField(fields, 'curriculumDetail', 'Détail cursus', proSit.curriculumDetail);
  return fields;
}

/** Professional-situation fields for an employee or entrepreneur. */
function getEmployeeOrEntrepreneurProFields(
  proSit: Record<string, any>,
  departments: DepartmentOption[],
): ParentProSitField[] {
  const fields: ParentProSitField[] = [];
  pushProSitField(
    fields,
    'fieldOfActivity',
    "Secteur d'activité",
    fieldOfActivityLabel(proSit.fieldOfActivity),
  );
  pushProSitField(
    fields,
    'specifiedActivityField',
    'Précision secteur',
    proSit.specifiedActivityField,
  );
  pushProSitField(fields, 'companyName', 'Entreprise', enterpriseName(proSit));
  pushProSitField(
    fields,
    'activityDepartment',
    "Département d'activité",
    departmentName(proSit.activityDepartment, departments),
  );
  return fields;
}

/** Professional-situation fields for a civil servant. */
function getCivilServantProFields(
  proSit: Record<string, any>,
  departments: DepartmentOption[],
): ParentProSitField[] {
  const fields: ParentProSitField[] = [];
  pushProSitField(
    fields,
    'fieldOfActivity',
    "Secteur d'activité",
    fieldOfActivityLabel(proSit.fieldOfActivity),
  );
  pushProSitField(fields, 'companyName', 'Organisme / Administration', enterpriseName(proSit));
  if (proSit.civilServantBranch) {
    pushProSitField(
      fields,
      'civilServantBranch',
      'Grande fonction publique',
      CIVIL_SERVICE_BRANCH_LABEL[proSit.civilServantBranch] ?? proSit.civilServantBranch,
    );
  }
  pushProSitField(
    fields,
    'activityDepartment',
    "Département d'activité",
    departmentName(proSit.activityDepartment, departments),
  );
  return fields;
}

/** Professional-situation fields for unemployed or retired. */
function getUnemployedOrRetiredProFields(proSit: Record<string, any>): ParentProSitField[] {
  const fields: ParentProSitField[] = [];
  pushProSitField(
    fields,
    'fieldOfActivity',
    "Secteur d'activité",
    fieldOfActivityLabel(proSit.fieldOfActivity),
  );
  return fields;
}

/** Flattened professional-situation rows for the expanded parent card. */
export function getParentProfessionalSituationFields(
  proSitRaw: unknown,
  departments: DepartmentOption[] = [],
): ParentProSitField[] | null {
  const proSit = flattenProfessionalSituation(proSitRaw as Record<string, unknown> | undefined);
  if (!proSit?.status) return null;

  const fields: ParentProSitField[] = [];
  if (proSit.status === 'high_school_student')
    fields.push(...getHighSchoolStudentProFields(proSit));
  if (proSit.status === 'student') fields.push(...getStudentProFields(proSit));
  if (proSit.status === 'employee' || proSit.status === 'entrepreneur') {
    fields.push(...getEmployeeOrEntrepreneurProFields(proSit, departments));
  }
  if (proSit.status === 'civil_servant')
    fields.push(...getCivilServantProFields(proSit, departments));
  if (proSit.status === 'unemployed' || proSit.status === 'retired') {
    fields.push(...getUnemployedOrRetiredProFields(proSit));
  }

  return fields.length > 0 ? fields : null;
}

/** Yes/no chip kind from contract `isValid`; null when unset. */
export function getContractValidKind(isValid: unknown): 'yes' | 'no' | null {
  if (isValid === true) return 'yes';
  if (isValid === false) return 'no';
  return null;
}

/** Section completion percent, or 0. */
export function getSectionCompletionPercent(section: unknown): number {
  const data = section as { sectionCompletion?: { completionPercentage?: number } } | undefined;
  return data?.sectionCompletion?.completionPercentage ?? 0;
}

/** Department name from a code string or `{ departmentId, departmentName }`. */
export function formatDepartmentValue(
  department: unknown,
  departments: DepartmentOption[],
): string | undefined {
  if (!department) return undefined;
  if (typeof department === 'string') {
    return departments.find((item) => item.code === department)?.name || department;
  }
  if (typeof department === 'object') {
    const dept = department as { departmentId?: string; departmentName?: string };
    return (
      dept.departmentName ||
      departments.find((item) => item.code === dept.departmentId)?.name ||
      dept.departmentId
    );
  }
  return undefined;
}
